import React from 'react';
import loadable from '@loadable/component';

import { array, bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { withTranslatedAssets } from '../WithTranslatedAssets/WithTranslatedAssets';

import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

export const LandingPageComponent = props => {
  const {
    pageAssetsData,
    inProgress,
    error,
    translationMeta,
    latestListings,
    latestListingsInProgress,
    latestListingsError,
  } = props;

  const addLatestListingsSectionAfterBrownBlock = data => {
    if (!data?.sections?.length) {
      return data;
    }

    const latestSectionId = 'latest-listings-feed';
    const alreadyAdded = data.sections.some(section => section.sectionId === latestSectionId);
    if (alreadyAdded) {
      return data;
    }

    // Find the brown block (customAppearance with backgroundColor #382d29)
    const brownBlockIndex = data.sections.findIndex(
      section =>
        section.appearance?.fieldType === 'customAppearance' &&
        section.appearance?.backgroundColor === '#382d29'
    );

    // Fallback to after hero if brown block not found
    const insertAfterIndex =
      brownBlockIndex >= 0
        ? brownBlockIndex
        : data.sections.findIndex(section => section.sectionType === 'hero');

    if (insertAfterIndex < 0) {
      return data;
    }

    const latestListingsSection = {
      sectionType: 'latestListings',
      sectionId: latestSectionId,
      sectionName: 'Latest listings feed',
      appearance: { fieldType: 'defaultAppearance' },
    };

    const sections = [...data.sections];
    sections.splice(insertAfterIndex + 1, 0, latestListingsSection);

    return { ...data, sections };
  };

  const rawPageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;
  const pageData = addLatestListingsSectionAfterBrownBlock(rawPageData);

  // Log translation info for debugging (remove in production)
  if (process.env.NODE_ENV === 'development' && translationMeta?.isTranslated) {
    console.log('ShareGardens LandingPage: Using translated content', {
      locale: translationMeta.currentLocale,
      assetsTranslated: translationMeta.assetsTranslated,
      cacheStats: translationMeta.cacheStats
    });
  }

  return (
    <PageBuilder
      pageAssetsData={pageData}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
      schemaType="WebPage"
      currentPage="LandingPage"
      options={{
        latestListings,
        latestListingsInProgress,
        latestListingsError,
      }}
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
  translationMeta: object, // Added for translation metadata
  latestListings: array,
  latestListingsInProgress: bool,
  latestListingsError: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const { latestListingIds = [], latestListingsInProgress, latestListingsError } =
    state.LandingPage || {};
  const latestListings = getListingsById(state, latestListingIds);

  return {
    pageAssetsData,
    inProgress,
    error,
    latestListings,
    latestListingsInProgress,
    latestListingsError,
  };
};

// Enhanced LandingPage with translation support
const LandingPageWithTranslation = compose(
  connect(mapStateToProps),
  withTranslatedAssets({
    enableTranslation: true,
    assetNames: [camelize(ASSET_NAME)], // Only translate the landing page asset  
    cacheTimeoutMs: 30 * 60 * 1000 // 30 minutes cache
  })
)(LandingPageComponent);

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = LandingPageWithTranslation;

export default LandingPage;